import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const getDesignationId = (d) => d?.designationId || d?._id || d?.id || "";
const getDesignationName = (d) =>
  String(d?.designationName || d?.name || "").trim();

const pickDesignationList = (data) => {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.designations)) return data.designations;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  return [];
};

const pickMember = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.member && typeof data.member === "object") return data.member;
  if (data.community && typeof data.community === "object")
    return data.community;
  if (
    data.result &&
    typeof data.result === "object" &&
    !Array.isArray(data.result)
  ) {
    return data.result;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  if (data._id || data.id || data.communityId || data.email || data.name)
    return data;
  return null;
};

const getMemberImage = (m) =>
  String(m?.image || m?.profileImage || m?.avatar || "").trim();

const getMemberMobile = (m) =>
  String(m?.mobileNumber || m?.phone || m?.mobile || "").trim();

const getMemberBlock = (m) => String(m?.blockNumber || m?.block || "").trim();

const normalizeStatus = (m) => {
  if (typeof m?.isActive === "boolean")
    return m.isActive ? "active" : "inactive";
  const s = String(m?.status || "")
    .trim()
    .toLowerCase();
  if (s === "inactive") return "inactive";
  return "active";
};

const readOnlyFieldStyle = {
  backgroundColor: "#f1f3f5",
  color: "#868e96",
  cursor: "not-allowed",
  borderColor: "#e9ecef",
};

const EXECUTIVE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "tenure_completion", label: "Tenure Completion" },
  { value: "resigned", label: "Resigned" },
  { value: "removed", label: "Removed" },
];

const normalizeExecutiveStatus = (m) => {
  const s = String(m?.executiveStatus || "")
    .trim()
    .toLowerCase();
  return EXECUTIVE_STATUS_OPTIONS.some((o) => o.value === s) ? s : "active";
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const formatDisplayDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const CommunityEdit = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const designationWrapRef = useRef(null);
  const designationSearchReqIdRef = useRef(0);

  const communityId = useMemo(() => {
    try {
      return idParam ? decodeURIComponent(String(idParam)).trim() : "";
    } catch {
      return String(idParam || "").trim();
    }
  }, [idParam]);

  const userId = useMemo(() => localStorage.getItem("userid") || "", []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [member, setMember] = useState(null);

  const [designation, setDesignation] = useState("");
  const [status, setStatus] = useState("active");
  const [designationSearch, setDesignationSearch] = useState("");
  const [designationSuggestions, setDesignationSuggestions] = useState([]);
  const [searchingDesignations, setSearchingDesignations] = useState(false);
  const [showDesignationSuggestions, setShowDesignationSuggestions] =
    useState(false);
  const [executiveStatus, setExecutiveStatus] = useState("active");
  const [periodCompletionDate, setPeriodCompletionDate] = useState("");

  useEffect(() => {
    if (!communityId) {
      toast.error("Invalid community member link.");
      navigate("/community", { replace: true });
    }
  }, [communityId, navigate]);

  useEffect(() => {
    if (!member) return;
    const original = String(member.designation || "").trim();
    if (designation.trim() && designation.trim() !== original) {
      setExecutiveStatus("active");
    }
  }, [designation, member]);

  const applyMemberToForm = useCallback((m) => {
    if (!m) return;
    const des = String(m?.designation || "").trim();
    setDesignation(des);
    setDesignationSearch(des);
    setStatus(normalizeStatus(m));
    setExecutiveStatus(normalizeExecutiveStatus(m));
    setPeriodCompletionDate(toDateInputValue(m?.periodCompletionDate));
  }, []);

  const fetchMember = useCallback(async () => {
    if (!API_BASE_URL || !communityId) {
      if (!API_BASE_URL)
        toast.error("Missing REACT_APP_API_URL in environment");
      setLoading(false);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const payload = {
        action: "community.getById",
        communityId,
      };
      if (userId) {
        payload.userId = userId;
        payload.userid = userId;
      }

      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const m = pickMember(data);
        if (m) {
          setMember(m);
          applyMemberToForm(m);
        } else {
          toast.error(
            data?.message || "Community member not found in response",
          );
          setMember(null);
        }
      } else {
        toast.error(data?.message || "Failed to load community member");
        setMember(null);
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Something went wrong",
      );
      setMember(null);
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, communityId, userId, applyMemberToForm]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  const applyDesignation = useCallback((row) => {
    if (!row) return;
    const name = getDesignationName(row);
    if (!name) return;
    setDesignation(name);
    setDesignationSearch(name);
    setDesignationSuggestions([]);
    setShowDesignationSuggestions(false);
  }, []);

  const searchDesignations = useCallback(
    async (query) => {
      const q = String(query || "").trim();
      if (!API_BASE_URL) {
        setDesignationSuggestions([]);
        setSearchingDesignations(false);
        return;
      }

      const reqId = ++designationSearchReqIdRef.current;
      setSearchingDesignations(true);
      try {
        const payload = {
          action: "designation.getAll",
          page: 1,
          limit: 50,
        };
        if (q) payload.search = q;
        if (userId) {
          payload.userId = userId;
          payload.userid = userId;
        }

        const response = await axios.post(`${API_BASE_URL}`, payload);
        const data = response?.data;
        if (reqId !== designationSearchReqIdRef.current) return;

        if (data?.status === "success" || data?.success === true) {
          const list = pickDesignationList(data);
          setDesignationSuggestions(Array.isArray(list) ? list : []);
          setShowDesignationSuggestions(true);
        } else {
          setDesignationSuggestions([]);
        }
      } catch (error) {
        if (reqId !== designationSearchReqIdRef.current) return;
        setDesignationSuggestions([]);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Designation search failed",
        );
      } finally {
        if (reqId === designationSearchReqIdRef.current) {
          setSearchingDesignations(false);
        }
      }
    },
    [API_BASE_URL, userId],
  );

  useEffect(() => {
    const q = designationSearch.trim();
    if (!showDesignationSuggestions) return undefined;
    const t = setTimeout(() => {
      searchDesignations(q);
    }, 350);
    return () => clearTimeout(t);
  }, [designationSearch, showDesignationSuggestions, searchDesignations]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!designationWrapRef.current?.contains(e.target)) {
        setShowDesignationSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const onDesignationInputChange = (e) => {
    const value = e?.target?.value ?? "";
    setDesignationSearch(value);
    setDesignation(value);
    setShowDesignationSuggestions(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const des = designation.trim() || designationSearch.trim();
    if (!des) {
      toast.error("Designation is required");
      return;
    }
    if (!status) {
      toast.error("Status is required");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!communityId) {
      toast.error("Missing community id");
      return;
    }

    setSaving(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const payload = {
        action: "community.update",
        communityId,
        designation: des,
        status,
        executiveStatus,
        periodCompletionDate: periodCompletionDate || null,
      };
      if (userId) {
        payload.userId = userId;
        payload.userid = userId;
      }

      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Community member updated successfully");
        navigate("/community");
      } else {
        toast.error(data?.message || "Failed to update community member");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setSaving(false);
    }
  };

  const name = member?.name || "—";
  const email = member?.email || "";
  const mobile = getMemberMobile(member);
  const block = getMemberBlock(member);
  const flatNumber = member?.flatNumber || "";
  const image = getMemberImage(member);
  const initials =
    String(name !== "—" ? name : "M")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() || "")
      .join("") || "M";

  return (
    <div className="employee-form community-create">
      <div className="content-header">
        <div className="container-fluid community-create-header">
          <div>
            <nav className="community-create-breadcrumb">
              <NavLink to="/community">Executive Committee </NavLink>
              <span>/</span>
              <span>Edit member</span>
            </nav>
            <h1 className="m-0 txt_lft">Edit Executive Committee </h1>
            <p className="community-create-sub">
              Update designation and status only.
            </p>
          </div>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <Link to="/community" className="btn btn-secondary">
              <i className="fa-solid fa-arrow-left mr-1" /> Back
            </Link>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading && !member ? (
            <div className="card">
              <div className="card-body text-muted">Loading member…</div>
            </div>
          ) : member ? (
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="row community-create-layout">
                <div className="col-12 col-lg-4 order-2 order-lg-2">
                  <div className="community-create-photo-card">
                    <div
                      className="community-create-photo-glow"
                      aria-hidden="true"
                    />
                    <div
                      className={`community-create-dropzone has-image`}
                      style={{ cursor: "default", pointerEvents: "none" }}
                    >
                      {image ? (
                        <img
                          src={image}
                          alt={name}
                          className="community-create-avatar-img"
                        />
                      ) : (
                        <div className="community-create-avatar-fallback">
                          {initials}
                        </div>
                      )}
                    </div>
                    <p className="community-create-photo-note mb-0">
                      Profile photo cannot be changed here
                    </p>
                  </div>
                </div>

                <div className="col-12 col-lg-8 order-1 order-lg-1">
                  <div className="community-create-main-card">
                    <section className="community-create-section">
                      <div className="community-create-section-head">
                        <span className="section-icon">
                          <i className="fa-solid fa-user" />
                        </span>
                        <div>
                          <h2>Member details</h2>
                          <p>Name and contact (read-only)</p>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12 col-md-6">
                          <div className="form-group">
                            <label className="form-label text-muted">
                              Name
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={name !== "—" ? name : ""}
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="form-group">
                            <label className="form-label text-muted">
                              Email
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              value={email}
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="form-group">
                            <label className="form-label text-muted">
                              Mobile
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={mobile}
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="form-group">
                            <label className="form-label text-muted">
                              Block
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={block}
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="form-group mb-0">
                            <label className="form-label text-muted">
                              Flat no.
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={flatNumber}
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="form-group mb-0">
                            <label className="form-label text-muted">
                              Date of occupancy
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={formatDisplayDate(member?.dateOfOccupancy)}
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>
                      </div>
                    </section>

                    <section className="community-create-section last">
                      <div className="community-create-section-head">
                        <span className="section-icon">
                          <i className="fa-solid fa-pen-to-square" />
                        </span>
                        <div>
                          <h2>Update</h2>
                          <p>Change designation and status</p>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12 col-md-6">
                          <div
                            className="form-group"
                            ref={designationWrapRef}
                            style={{ position: "relative" }}
                          >
                            <label className="form-label">
                              Position <span className="required-star">*</span>
                            </label>
                            <div className="input-group community-create-email-group">
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search designation..."
                                value={designationSearch}
                                onChange={onDesignationInputChange}
                                onFocus={() => {
                                  setShowDesignationSuggestions(true);
                                  if (!designationSuggestions.length) {
                                    searchDesignations(
                                      designationSearch.trim(),
                                    );
                                  }
                                }}
                                required
                                autoComplete="off"
                                disabled={saving}
                              />
                              <div className="input-group-append">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  disabled={searchingDesignations || saving}
                                  onClick={() =>
                                    searchDesignations(designationSearch.trim())
                                  }
                                  title="Search designations"
                                >
                                  {searchingDesignations ? (
                                    <i className="fa-solid fa-spinner fa-spin" />
                                  ) : (
                                    <i className="fa-solid fa-magnifying-glass" />
                                  )}
                                </button>
                              </div>
                            </div>

                            {showDesignationSuggestions &&
                            designationSuggestions.length > 0 ? (
                              <div className="community-create-suggestions">
                                {designationSuggestions.map((d, idx) => {
                                  const dName = getDesignationName(d);
                                  const id =
                                    getDesignationId(d) || `des-${idx}`;
                                  return (
                                    <button
                                      key={id}
                                      type="button"
                                      className="community-create-suggestion"
                                      onClick={() => applyDesignation(d)}
                                    >
                                      <div className="sug-name">
                                        {dName || "Designation"}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            ) : null}

                            {showDesignationSuggestions &&
                            !searchingDesignations &&
                            designationSearch.trim() &&
                            designationSuggestions.length === 0 ? (
                              <div className="community-create-suggestions empty">
                                No designations found
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="form-group">
                            <label className="form-label">
                              Executive status{" "}
                              <span className="required-star">*</span>
                            </label>
                            <select
                              className="form-control"
                              value={executiveStatus}
                              onChange={(e) =>
                                setExecutiveStatus(e.target.value)
                              }
                              disabled={saving}
                              required
                            >
                              {EXECUTIVE_STATUS_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="form-group">
                            <label className="form-label text-muted">
                              From date
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                designation.trim() !==
                                String(member?.designation || "").trim()
                                  ? "Will be set on save"
                                  : formatDisplayDate(
                                      member?.fromDate || member?.createdAt,
                                    )
                              }
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="form-group mb-0">
                            <label className="form-label text-muted">
                              Date of Period Completion
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={
                                executiveStatus !==
                                normalizeExecutiveStatus(member)
                                  ? executiveStatus === "active"
                                    ? ""
                                    : "Will be set on save"
                                  : formatDisplayDate(member?.toDate)
                              }
                              readOnly
                              tabIndex={-1}
                              style={readOnlyFieldStyle}
                            />
                          </div>
                        </div>
                      </div>
                    </section>

                    <div className="community-create-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={saving}
                        onClick={() => navigate("/community")}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary community-create-submit"
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            <div className="card">
              <div className="card-body text-muted">
                Could not load member. <Link to="/community">Back to list</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommunityEdit;
